import React, { useEffect, useState } from "react";
import "./Consent.css";
import ConsentContent from "../../../Shared/Components/ConsentContent/ConsentContent";
import { useNavigate } from "react-router-dom";
import { generateSpeech, saveConsent } from "../../../Actions/ChatActions";
import { getTooltip, getUserToken, saveConsentFlag, stopPlayingAudio,
  handleTooltipOnboarding,
  CheckLanguageSupport,
  setLanguageChangeFlag,
  isNeGDDeployment,
} from "../../../Actions/Utils";
import ToastMessage from "../../../Shared/Components/ToastMessage/ToastMessage";
import { ToastSeverityMap } from "../../../NewConstants/ToastConstants";
import { consent, consent_title, consentButton, consentAudio, consent_content, consent_headers, consnent_data, consnent_data_NeGD, consent_content_NeGD, consentAudioNeGD } from "../../../NewConstants/ConsentConstant";
import { languageLabel } from "../../../NewConstants/Language-Constants";
import Markdown from 'react-markdown'
import PwaInstallBanner from "../../../Shared/Components/PwaInstallBanner/PwaInstallBanner";
import { navTitle } from "../../../NewConstants/NavConstants";

const Consent = ({ selectedLanguage, audioRef, setStepIndex, setStartTour }) => {
  const [consentGiven, setConsentGiven] = useState(false);
  const [showSpeakerActive, setShowSpeakerActive] = useState(true);
  const [requestController, setRequestController] = useState(null);
  const navigate = useNavigate();
  const hideHeaderIconFlag = process.env.REACT_APP_HIDE_HEADER_ICON === 'true';
  const skipOnboardingFlag = process.env.REACT_APP_SKIP_ONBOARDING === 'true';
  const [accessToken, setAccessToken] = useState(null);
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [toastErrorMessage, setToastErrorMessage] = useState("");

  useEffect(() => {
    const token = getUserToken();
    setAccessToken(token);
    if (!token) {
      sessionStorage.clear();
      localStorage.clear();
      navigate(isNeGDDeployment() ? "/app-error" : "/login");
    }
    const handleVisibilityChange = () => {
      stopPlayingAudio(audioRef);
      setShowSpeakerActive(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.history.pushState(null, document.title, window.location.href);
    const handlePopState = (event) => {
      window.history.pushState(null, document.title, window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
    }
  }, []);

  useEffect(() => {
    const handleAudio = async () => {
      const res = await generateSpeech(isNeGDDeployment() ? consentAudioNeGD[selectedLanguage] : consentAudio[selectedLanguage], selectedLanguage);
      if (res?.data?.url) {
        const newAudio = new Audio(res?.data?.url);
        if (audioRef.current) {
          audioRef.current.pause();
        }
        if (requestController) {
          requestController.abort();
        }

        audioRef.current = newAudio;
        newAudio.play().catch((err) => {
          console.error("Error playing audio:", err);
        });
      } else {
        setToastErrorMessage(res?.data?.detail)
        setShowToastMessage(true);
        setShowSpeakerActive(false);
      }
    };

    if (showSpeakerActive) handleAudio();
  }, [showSpeakerActive]);

  const handleConsentProceed = async () => {
    const res = await saveConsent(accessToken, true, selectedLanguage);
    if (res?.status === 200) {
      saveConsentFlag(true);
      stopPlayingAudio(audioRef);
      if (isNeGDDeployment()) {
        const tooltip = getTooltip();
        handleTooltipOnboarding(tooltip, setStepIndex, setStartTour);
        navigate("/chat");
      } else {
        await navigateBasedOnSupport();
      }
    }
    if (res?.status === 401) {
      localStorage.clear();
      sessionStorage.clear();
      navigate(isNeGDDeployment() ? "/app-error" : "/login");
    }
    if (res?.status === 500) {
      setToastErrorMessage(res?.data?.detail)
      setShowToastMessage(true);
    }
  };
  const backArrowClick = () => {
    navigate("/guide");
  };

  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.error("getUserMedia error:", err);
      setToastErrorMessage("Please allow microphone");
      setShowToastMessage(true);
    }
  };
  const navigateBasedOnSupport = async () => {
    const route = await CheckLanguageSupport();
    if (route === "chat"){
      const tooltip = getTooltip();
      handleTooltipOnboarding(tooltip, setStepIndex, setStartTour);
      navigate("/chat");
    }else if (route === "language-check"){
      navigate("/language-check");
    }else{
      navigate(isNeGDDeployment() ? "/app-error" : "/login");
    }
  };
  const handleLanguageChange = () => {
    setLanguageChangeFlag("false");
    stopPlayingAudio(audioRef);
    navigate("/lang");
  };

  return (
    <div className="h-screen-safe w-screen flex flex-col">
      <PwaInstallBanner selectedLanguage={selectedLanguage} />
      {isNeGDDeployment() ? (
          <div className="flex justify-between items-center pt-2 px-4 z-0" style={{backgroundImage: "url(/Assets/Images/NavBackground.svg)", height: "3.75rem"}}>
          </div>
        ) : (
          <div className="h-[3.75rem] py-4 w-screen text-lg text-[var(--primary-dark)] font-semibold flex justify-center items-center bg-[var(--neutral-white)]" >{navTitle[selectedLanguage]}</div>
        )}
      <div className="consent-page pb-safe w-screen flex-1 overflow-y-auto">
        <div className="flex flex-col px-4 py-6 gap-5">
          <div className="flex flex-col items-center py-2 px-1.5 rounded-2xl bg-[var(--neutral-white)] border border-[var(--border-light)] shadow-lg">
            <div className="w-full flex items-center justify-between px-3"> 
              <div className="flex items-center" onClick={isNeGDDeployment() ? undefined : handleLanguageChange}>
                <img src={isNeGDDeployment() ? "/Assets/Images/GlobeRed.svg" : "/Assets/Images/GlobeBlue.svg"} alt="globe" className="w-5 h-5 mr-2"/>
                <span className="text-[color:var(--primary-dark)] font-bold text-md">{languageLabel[selectedLanguage] || ""}</span>
                <svg className="ml-2 w-4 h-4" fill="none" stroke="var(--primary-dark)" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </div>
              <div>
              {showSpeakerActive ? (
                <div
                  className="bg-[var(--primary-light-3)] rounded-full h-12 w-12 flex items-center justify-center p-1"
                  onClick={() => {
                    setShowSpeakerActive(!showSpeakerActive);
                    stopPlayingAudio(audioRef);
                  }}
                >
                  <div className="bg-[var(--primary-dark)] rounded-full flex items-center justify-center h-[100%] w-[100%]">
                    <img
                      src={"/Assets/Images/whiteSpeaker.svg"}
                      alt="Active Speaker"
                    />
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => {
                    setShowSpeakerActive(!showSpeakerActive);
                  }}
                >
                  <img
                    src={"/Assets/Images/crossSpeakerIcon.svg"}
                    alt="Inactive Speaker"
                  />
                </div>
              )}
              </div>

            </div>
            <div className="h-full w-full flex flex-col items-center">
              <img src="/Assets/Images/Lady_Illustrations/Consent.svg" alt="Illustration" className="w-full h-[13rem]" />
            </div>
            {/* <div className="image-div rounded-lg"></div> */}
            <div className='text-[var(--almost-black)] font-["Noto_Sans"] font-bold text-lg tracking-wide leading-relaxed mt-4'>
              {consent_headers.header[selectedLanguage]}
            </div>
            <div className='text-[var(--dark-neutral-2)] font-["Noto_Sans"] text-base font-normal leading-double tracking-wide'>
              <div className="flex p-4 text-sm">
                {consent_headers.subheader[selectedLanguage]}
              </div>
              <div className="divider" />
            </div>
            {(isNeGDDeployment() ? consent_content_NeGD : consent_content).map((itm, idx, arr) => {
              return (
                <div key={idx} className="w-full flex flex-col py-4 px-5 gap-5">
                  <ConsentContent
                    icon={itm.image}
                    header={itm.header[selectedLanguage]}
                    text={itm.text[selectedLanguage]}
                  />
                  {idx < arr.length - 1 ? (
                    <div className="divider" />
                  ) : null}
                </div>
              );
            })}
            <div className="rounded-lg bg-[var(--light-neutral-2)] py-5 px-7 flex flex-col gap-2.5">
              <div className="text-[color:var(--black)] text-sm font-medium font-['Noto_Sans']">
                {isNeGDDeployment() ? consnent_data_NeGD.question[selectedLanguage] : consnent_data.question[selectedLanguage]}
              </div>
              <div className="text-[color:var(--dark-neutral-2)] text-sm font-normal font-['Noto_Sans']">
                <Markdown>{isNeGDDeployment() ? consnent_data_NeGD.text[selectedLanguage] : consnent_data.text[selectedLanguage]}</Markdown>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div
              className={`consent-input-wrapper ${consentGiven ? "consent-given" : ""
                }`}
            >
              <input
                type="checkbox"
                className="custom-checkbox"
                value={consentGiven}
                onChange={async () => {
                  setConsentGiven(!consentGiven);
                  if (!consentGiven) {
                    requestMicrophonePermission();
                  }
                }}
              />
              <div className="text-sm font-normal text-[color:var(--black)]">
                {consent[selectedLanguage]}
              </div>
            </div>
            {consentGiven && (
              <div
                className='w-full rounded-lg border border-[var(--light-neutral-4)] bg-[var(--primary-dark)] flex gap-2 py-3 px-4 justify-center items-center text-[color:var(--neutral-white)] text-base font-normal font-["Roboto"]'
                onClick={() => handleConsentProceed()}
              >
                {consentButton[selectedLanguage]}
                <img src={"/Assets/Images/nextWhiteIcon.svg"} alt="proceed" />
              </div>
            )}
          </div>
        </div>
      </div>
      {
        showToastMessage && <ToastMessage message={toastErrorMessage} severity={ToastSeverityMap.error} direction={"bottom"} close={setShowToastMessage}/>
      }
    </div>
  );
};

export default Consent;
